import React from 'react'

import { useHandler } from '@surglogs-app/hooks'
import { UploadSignatureAction } from '@surglogs-app/network'
import { usePrompt } from '@surglogs-app/prompt'
import { useToast } from '@surglogs-app/ui.toast'

import { stringToBlob } from '../services'
import { SignaturePadDialog } from '../SignaturePadDialog'

interface SignatureHandlersProps {
  onChange: (value: any) => void
  beforeSignatureAdd?: () => Promise<boolean>
  uploadAction: UploadSignatureAction
}

interface PropsWithUserId extends SignatureHandlersProps {
  userId: number
  userName?: never
}

interface PropsWithUserName extends SignatureHandlersProps {
  userName: string
  userId?: never
}

type Props = PropsWithUserName | PropsWithUserId

export const useSignatureHandlers = ({
  onChange,
  uploadAction,
  beforeSignatureAdd,
  userId,
  userName,
}: Props) => {
  const { showErrorToast } = useToast()

  const onConfirm = useHandler(async (data: string) => {
    onChange(data)
    if (data) {
      const blob = stringToBlob(data)

      try {
        const value = await uploadAction({
          file: blob,
          opts:
            userName && (!userId || Number(userId) === -1)
              ? { userName }
              : { userId: Number(userId) },
        })

        onChange(value)
      } catch (e) {
        showErrorToast({ error: e })
        onChange(null)
      }
    }
  })

  const [prompt] = usePrompt({ disableCloseOnBlur: true })
  const add = useHandler(async () => {
    if (
      !beforeSignatureAdd ||
      (typeof beforeSignatureAdd === 'function' && (await beforeSignatureAdd()))
    ) {
      return prompt(({ answer }) => (
        <SignaturePadDialog {...{ onConfirm, hideDialog: answer }} />
      ))
    }
  })

  const remove = useHandler(() => onChange(null))

  return [add, remove] as const
}
